import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

// Copied from d2d-XDR's src/components/Sidebar.tsx (bafce6c, cascade plan
// 4.8): the app-level behaviour it adds around ds/AppSidebar, which stays
// byte-identical to upstream. Layout.tsx attaches both hooks to its sidebar.
// Styles: .rc-railtip in src/styles/zta.css. React 18 here, so the ref types
// are RefObject<HTMLDivElement> where d2d-XDR (React 19) writes `| null`.


// ── Collapsed-rail tooltip ──────────────────────────────────────────────────
// The collapsed rail is icon-only, so hovering (or keyboard-focusing) a row
// pops its name out to the right of the rail. It is portalled to <body> and
// positioned from the row's own rect, because the rail's scroll area is
// `overflow-x: hidden` and would clip anything drawn inside it. AppSidebar
// (ds/, kept identical to upstream) still sets a native `title` on each row
// while collapsed; it is moved aside on hover so the browser's own tooltip
// doesn't appear on top of this one, and it stays for assistive tech until
// then.
const RAIL_ROWS = ".rc-sidenav__item, .rc-sidenav__collapse, .rc-sidenav__brand";

function railRowLabel(row: HTMLElement): string {
  const text =
    row.querySelector(".rc-sidenav__item-label, .rc-sidenav__brand-title")?.textContent?.trim() ||
    row.dataset.railTip ||
    row.getAttribute("title") ||
    row.getAttribute("aria-label") ||
    "";
  if (row.hasAttribute("title")) {
    row.dataset.railTip = row.getAttribute("title") ?? "";
    row.removeAttribute("title");
  }
  return text;
}

export function useRailTooltip(collapsed: boolean, mounted: boolean): { ref: RefObject<HTMLDivElement>; tip: ReactNode } {
  const ref = useRef<HTMLDivElement>(null);
  // Each row gets its own tooltip that slides in from the LEFT (out of the
  // rail) on a spring; `seq` re-keys the element per row so that entrance
  // replays. It stays mounted after the pointer leaves so it can fade back
  // out toward the rail instead of vanishing.
  const [tip, setTip] = useState<{ label: string; top: number; left: number; open: boolean; seq: number } | null>(null);

  useEffect(() => {
    const root = ref.current;
    if (!collapsed || !root) {
      setTip((t) => (t ? { ...t, open: false } : t));
      return;
    }
    let hideTimer = 0;
    const show = (target: EventTarget | null) => {
      const row = target instanceof Element ? target.closest<HTMLElement>(RAIL_ROWS) : null;
      if (!row || !root.contains(row)) return;
      const label = railRowLabel(row);
      if (!label) return;
      window.clearTimeout(hideTimer);
      const r = row.getBoundingClientRect();
      const top = r.top + r.height / 2;
      const left = root.getBoundingClientRect().right + 10;
      setTip((t) => {
        if (t?.open && t.label === label && t.top === top) return t;
        return { label, top, left, open: true, seq: (t?.seq ?? 0) + 1 };
      });
    };
    // A short grace period: moving from one icon to the next fires pointerout
    // before pointerover, and hiding on the spot would make the tooltip blink.
    const hide = () => {
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => setTip((t) => (t ? { ...t, open: false } : t)), 90);
    };
    const onOver = (e: PointerEvent) => show(e.target);
    const onFocus = (e: FocusEvent) => show(e.target);
    const onOut = (e: PointerEvent) => {
      const next = e.relatedTarget instanceof Element ? e.relatedTarget.closest(RAIL_ROWS) : null;
      if (!next || !root.contains(next)) hide();
    };
    root.addEventListener("pointerover", onOver);
    root.addEventListener("pointerout", onOut);
    root.addEventListener("focusin", onFocus);
    root.addEventListener("focusout", hide);
    root.addEventListener("scroll", hide, true);
    return () => {
      window.clearTimeout(hideTimer);
      root.removeEventListener("pointerover", onOver);
      root.removeEventListener("pointerout", onOut);
      root.removeEventListener("focusin", onFocus);
      root.removeEventListener("focusout", hide);
      root.removeEventListener("scroll", hide, true);
    };
  }, [collapsed, mounted]);

  const node =
    tip && typeof document !== "undefined"
      ? createPortal(
          <div
            key={tip.seq}
            className="rc-railtip"
            role="tooltip"
            aria-hidden={!tip.open}
            data-open={tip.open || undefined}
            style={{ top: tip.top, left: tip.left }}
          >
            {tip.label}
          </div>,
          document.body
        )
      : null;
  return { ref, tip: node };
}

// ── Smooth hover highlight ──────────────────────────────────────────────────
// AppSidebar (ds/, identical to upstream) slides one hover pill under the
// row being pointed at, but it hides the pill whenever the pointer is over
// anything that is not a row — the 4px gaps between rows and the section
// labels — so sweeping down the rail made the highlight blink off and on.
// A capture-phase listener on the sidebar root sees each mouseover before
// AppSidebar's own handler does and:
//   · swallows it when it lands between rows, so the pill simply stays on
//     the last row until the pointer reaches the next one (or leaves the
//     list, which AppSidebar still handles);
//   · flags the rail with data-pill-jump when the pill is currently hidden,
//     so it appears in place (fade + scale on a spring) rather than sliding
//     in from wherever it was last. The flag lives for two frames, enough for
//     the new position to be committed without a transition.
export function useSmoothHoverPill(ref: RefObject<HTMLDivElement>, mounted: boolean) {
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    let frame = 0;
    const onOverCapture = (e: MouseEvent) => {
      const items = root.querySelector(".rc-sidenav__items");
      const target = e.target instanceof Element ? e.target : null;
      if (!items || !target || !items.contains(target)) return;
      if (!target.closest(".rc-sidenav__item")) {
        e.stopPropagation();
        return;
      }
      const pill = root.querySelector<HTMLElement>(".rc-sidenav__pill--hover");
      const rail = root.querySelector<HTMLElement>(".rc-sidenav__rail");
      if (pill && rail && pill.style.opacity !== "1") {
        rail.dataset.pillJump = "true";
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          frame = requestAnimationFrame(() => {
            delete rail.dataset.pillJump;
          });
        });
      }
    };
    root.addEventListener("mouseover", onOverCapture, true);
    return () => {
      cancelAnimationFrame(frame);
      root.removeEventListener("mouseover", onOverCapture, true);
    };
  }, [ref, mounted]);
}
